import { MapPin, Heart, Users, Mail, Phone } from 'lucide-react';
import { useState } from 'react';

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // her kan du senere sende til backend / supabase / email-service
    setSubmitting(true);
    setSubmitted(true);
    setFormData({ name: '', email: '', message: '' });
    setTimeout(() => {
      setSubmitted(false);
      setSubmitting(false);
    }, 2500);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <img src="/bukken.png" alt="Væren Leverer" className="h-24 w-24 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Om Gjesdalivery
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Vi er en lokal leveringstjeneste som støtter lokale restauranter og butikker, og skaper
            jobbmuligheter for ungdom i Gjesdal.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full mb-4">
              <MapPin className="w-6 h-6 text-[#004D99] dark:text-orange-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Lokalt forankret</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Vi kjenner Gjesdal og lokalsamfunnet vårt inn og ut.
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full mb-4">
              <Heart className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Støtter lokale</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Vi samarbeider med restauranter og butikker i området. Vil du selge hos oss? Ta kontakt!
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full mb-4">
              <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Skaper jobber</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Vi gir ungdom i Gjesdal jobbmuligheter, med svært gode lønnsmuligheter.
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Vår historie</h2>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Gjesdalivery ble startet av lokale ungdommer som så et behov for en enkel og pålitelig
              leveringstjeneste i Gjesdal. Vi visste hvor vanskelig det kunne være å finne gode jobber
              som ungdom, og vi ville gjøre noe med det.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              I dag leverer vi mat og dagligvarer fra de beste restaurantene og butikkene i området,
              samtidig som vi gir lokale ungdommer muligheten til å tjene penger på sine egne premisser.
              Vi er stolte av Gjesdal-røttene våre, og av støtten vi gir lokale bedrifter.
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              Enten det er dårlig vær eller bare en lat søndag, leverer Væren alltid.
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Leveringsområde</h2>
          <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg mb-4 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-[#004D99] dark:text-orange-400 mx-auto mb-2" />
              <p className="text-gray-600 dark:text-gray-400">Ålgård, Gjesdal kommune</p>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-center">
            Vi leverer i hele Gjesdal kommune, med spesielt gode priser i Ålgård sentrum.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Kontakt oss</h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="flex items-start gap-3">
              <Mail className="w-6 h-6 text-[#004D99] dark:text-orange-400 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">E-post</h3>
                <a
                  href="mailto:kontakt@vaerenleverer.no"
                  className="text-[#004D99] dark:text-orange-400 hover:underline"
                >
                  kontakt@gjesdalivery.no
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-6 h-6 text-[#004D99] dark:text-orange-400 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">Telefon</h3>
                <a
                  href="tel:+4790976011"
                  className="text-[#004D99] dark:text-orange-400 hover:underline"
                >
                  +47 909 76 011
                </a>
              </div>
            </div>
          </div>

          {submitted && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-200">
              Takk for meldingen! Vi tar kontakt snart.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Navn
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-gray-900 dark:text-white"
                placeholder="Ola Nordmann"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                E-post
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-gray-900 dark:text-white"
                placeholder="ola@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Melding
              </label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-gray-900 dark:text-white"
                placeholder="Hva kan vi hjelpe deg med?"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-3 font-bold rounded-lg transition-colors ${
                submitting
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-[#004D99] hover:bg-[#003366] text-white'
              }`}
            >
              {submitting ? 'Sender…' : 'Send melding'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
